import { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";

// Countries list
const COUNTRIES = [
  "Canada", "United States", "United Kingdom", "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", 
  "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain",
  "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina",
  "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Cape Verde",
  "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia",
  "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "East Timor",
  "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Ethiopia", "Fiji", "Finland",
  "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea",
  "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq",
  "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "North Korea",
  "South Korea", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein",
  "Lithuania", "Luxembourg", "Macedonia", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta",
  "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia",
  "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand",
  "Nicaragua", "Niger", "Nigeria", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea",
  "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
  "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia",
  "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Swaziland", "Sweden", "Switzerland",
  "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia",
  "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "Uruguay", "Uzbekistan",
  "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

// Country phone codes
const COUNTRY_CODES = [
  { country: "Canada", code: "+1", flag: "🇨🇦" },
  { country: "United States", code: "+1", flag: "🇺🇸" },
  { country: "United Kingdom", code: "+44", flag: "🇬🇧" },
  { country: "Afghanistan", code: "+93", flag: "🇦🇫" },
  { country: "Albania", code: "+355", flag: "🇦🇱" },
  { country: "Algeria", code: "+213", flag: "🇩🇿" },
  { country: "Argentina", code: "+54", flag: "🇦🇷" },
  { country: "Australia", code: "+61", flag: "🇦🇺" },
  { country: "Austria", code: "+43", flag: "🇦🇹" },
  { country: "Bahrain", code: "+973", flag: "🇧🇭" },
  { country: "Bangladesh", code: "+880", flag: "🇧🇩" },
  { country: "Belgium", code: "+32", flag: "🇧🇪" },
  { country: "Brazil", code: "+55", flag: "🇧🇷" },
  { country: "China", code: "+86", flag: "🇨🇳" },
  { country: "Denmark", code: "+45", flag: "🇩🇰" },
  { country: "Egypt", code: "+20", flag: "🇪🇬" },
  { country: "France", code: "+33", flag: "🇫🇷" },
  { country: "Germany", code: "+49", flag: "🇩🇪" },
  { country: "India", code: "+91", flag: "🇮🇳" },
  { country: "Indonesia", code: "+62", flag: "🇮🇩" },
  { country: "Iran", code: "+98", flag: "🇮🇷" },
  { country: "Iraq", code: "+964", flag: "🇮🇶" },
  { country: "Ireland", code: "+353", flag: "🇮🇪" },
  { country: "Italy", code: "+39", flag: "🇮🇹" },
  { country: "Japan", code: "+81", flag: "🇯🇵" },
  { country: "Jordan", code: "+962", flag: "🇯🇴" },
  { country: "Kuwait", code: "+965", flag: "🇰🇼" },
  { country: "Lebanon", code: "+961", flag: "🇱🇧" },
  { country: "Malaysia", code: "+60", flag: "🇲🇾" },
  { country: "Mexico", code: "+52", flag: "🇲🇽" },
  { country: "Morocco", code: "+212", flag: "🇲🇦" },
  { country: "Netherlands", code: "+31", flag: "🇳🇱" },
  { country: "New Zealand", code: "+64", flag: "🇳🇿" },
  { country: "Nigeria", code: "+234", flag: "🇳🇬" },
  { country: "Norway", code: "+47", flag: "🇳🇴" },
  { country: "Oman", code: "+968", flag: "🇴🇲" },
  { country: "Pakistan", code: "+92", flag: "🇵🇰" },
  { country: "Palestine", code: "+970", flag: "🇵🇸" },
  { country: "Philippines", code: "+63", flag: "🇵🇭" },
  { country: "Poland", code: "+48", flag: "🇵🇱" },
  { country: "Portugal", code: "+351", flag: "🇵🇹" },
  { country: "Qatar", code: "+974", flag: "🇶🇦" },
  { country: "Russia", code: "+7", flag: "🇷🇺" },
  { country: "Saudi Arabia", code: "+966", flag: "🇸🇦" },
  { country: "Singapore", code: "+65", flag: "🇸🇬" },
  { country: "South Africa", code: "+27", flag: "🇿🇦" },
  { country: "South Korea", code: "+82", flag: "🇰🇷" },
  { country: "Spain", code: "+34", flag: "🇪🇸" },
  { country: "Sri Lanka", code: "+94", flag: "🇱🇰" },
  { country: "Sweden", code: "+46", flag: "🇸🇪" },
  { country: "Switzerland", code: "+41", flag: "🇨🇭" },
  { country: "Syria", code: "+963", flag: "🇸🇾" },
  { country: "Thailand", code: "+66", flag: "🇹🇭" },
  { country: "Turkey", code: "+90", flag: "🇹🇷" },
  { country: "United Arab Emirates", code: "+971", flag: "🇦🇪" },
  { country: "Yemen", code: "+967", flag: "🇾🇪" }
];

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    country: "Canada",
    countryCode: "+1",
    telephone: "",
    countryCode2: "+1",
    telephone2: "",
    password: "",
    confirmPassword: ""
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push("At least 8 characters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("At least one uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("At least one lowercase letter");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("At least one number");
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push("At least one special character");
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validate password on change
    if (name === 'password') {
      setPasswordErrors(validatePassword(value));
    }
  };

  const handleCountryChange = (country) => {
    setFormData({ ...formData, country });
  };

  const handleCountryCodeChange = (code) => {
    setFormData({ ...formData, countryCode: code });
  };

  const handleCountryCode2Change = (code) => {
    setFormData({ ...formData, countryCode2: code });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password complexity
    const errors = validatePassword(formData.password);
    if (errors.length > 0) {
      toast.error("Password does not meet complexity requirements");
      setPasswordErrors(errors);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const fullPhone = `${formData.countryCode} ${formData.telephone}`;
      const fullPhone2 = formData.telephone2 ? `${formData.countryCode2} ${formData.telephone2}` : "";
      const response = await axios.post(`${API}/auth/register`, {
        name: formData.name,
        email: formData.email,
        telephone: fullPhone,
        telephone2: fullPhone2,
        country: formData.country,
        password: formData.password
      });
      
      login(response.data.token, response.data.user);
      toast.success("Registration successful!");
      navigate('/application');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Registration failed";
      
      // Check if it's an "email already exists" error
      if (errorMessage.toLowerCase().includes("email is already registered")) {
        toast.error(
          <div>
            <p className="font-semibold">Email Already Registered</p>
            <p className="text-sm mt-1">This email is already in our system.</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-2 text-[#F7B538] underline hover:text-[#e5a627] font-medium"
            >
              Go to Existing Applicant Dashboard →
            </button>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0E366A] via-[#1a4d7a] to-[#0E366A] flex flex-col">
      <div className="bg-[#F7B538] py-2 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="text-sm text-[#0E366A] font-medium">Welcome to London Islamic School</div>
        </div>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/lis_logo.png" alt="London Islamic School Logo" className="mx-auto mb-4 h-32 w-auto"/>
            <h1 className="text-3xl font-serif text-white mb-2">London Islamic School</h1>
            <p className="text-[#F7B538] text-sm italic">Learning today. Leading tomorrow. Inspired Forever.</p>
          </div>

          <Button 
          data-testid="back-btn"
          variant="ghost" 
          onClick={() => navigate('/')}
          className="text-white hover:text-[#F7B538] hover:bg-white/10 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>
        
        <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur">
          <CardHeader className="space-y-1 bg-[#0E366A] text-white rounded-t-lg">
            <CardTitle className="text-2xl font-bold text-center">Parent Registration</CardTitle>
            <CardDescription className="text-center text-white/90">Create an account to apply for enrollment</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input 
                  data-testid="name-input"
                  id="name" 
                  name="name" 
                  required 
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input 
                  data-testid="email-input"
                  id="email" 
                  name="email" 
                  type="email" 
                  required 
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Select value={formData.country} onValueChange={handleCountryChange}>
                  <SelectTrigger id="country">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="telephone">Primary Telephone *</Label>
                <div className="flex gap-2">
                  <Select value={formData.countryCode} onValueChange={handleCountryCodeChange}>
                    <SelectTrigger className="w-[100px]" id="countryCode">
                      <SelectValue>
                        {COUNTRY_CODES.find(c => c.code === formData.countryCode)?.flag || "🏳️"} {formData.countryCode}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {COUNTRY_CODES.map((item) => (
                        <SelectItem key={item.code + item.country} value={item.code}>
                          {item.flag} {item.code} {item.country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input 
                    data-testid="telephone-input"
                    id="telephone" 
                    name="telephone" 
                    type="tel" 
                    required 
                    placeholder="123-456-7890"
                    value={formData.telephone}
                    onChange={handleChange}
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="telephone2">Secondary Telephone (Optional)</Label>
                <div className="flex gap-2">
                  <Select value={formData.countryCode2} onValueChange={handleCountryCode2Change}>
                    <SelectTrigger className="w-[100px]" id="countryCode2">
                      <SelectValue>
                        {COUNTRY_CODES.find(c => c.code === formData.countryCode2)?.flag || "🏳️"} {formData.countryCode2}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {COUNTRY_CODES.map((item) => (
                        <SelectItem key={item.code + item.country + "2"} value={item.code}>
                          {item.flag} {item.code} {item.country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input 
                    data-testid="telephone2-input"
                    id="telephone2" 
                    name="telephone2" 
                    type="tel" 
                    placeholder="123-456-7890"
                    value={formData.telephone2}
                    onChange={handleChange}
                    className="flex-1"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input 
                    data-testid="password-input"
                    id="password" 
                    name="password" 
                    type={showPassword ? "text" : "password"}
                    required 
                    value={formData.password}
                    onChange={handleChange}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formData.password && passwordErrors.length > 0 && (
                  <div className="text-xs text-red-600 space-y-1 mt-2">
                    <p className="font-semibold">Password must include:</p>
                    {passwordErrors.map((error, idx) => (
                      <p key={idx}>• {error}</p>
                    ))}
                  </div>
                )}
                {formData.password && passwordErrors.length === 0 && (
                  <p className="text-xs text-green-600 mt-2">✓ Password meets all requirements</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <div className="relative">
                  <Input 
                    data-testid="confirm-password-input"
                    id="confirmPassword" 
                    name="confirmPassword" 
                    type={showConfirmPassword ? "text" : "password"}
                    required 
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-red-600 mt-2">• Passwords do not match</p>
                )}
                {formData.confirmPassword && formData.password === formData.confirmPassword && (
                  <p className="text-xs text-green-600 mt-2">✓ Passwords match</p>
                )}
              </div>
              
              <Button 
                data-testid="register-submit-btn"
                type="submit" 
                className="w-full bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold" 
                disabled={loading}
              >
                {loading ? "Registering..." : "Register"}
              </Button>
            </form>
            
            <div className="mt-4 text-center space-y-2">
              <p className="text-sm text-gray-600">
                Already have an account?{" "}
                <Link to="/login" className="text-[#0E366A] hover:text-[#F7B538] font-semibold hover:underline">
                  Login here
                </Link>
              </p>
              <p className="text-xs text-gray-500">
                Existing applicants can use the login page to access the dashboard and manage applications.
              </p>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;